import React, { createContext, useContext, useEffect, useState } from 'react';

// Create context for Event System data cache
const EventSystemCacheContext = createContext();

// Custom hook to use the cache
export const useEventSystemCache = () => useContext(EventSystemCacheContext);

// Provider component
export const EventSystemCacheProvider = ({ children }) => {
  const [cachedData, setCachedData] = useState({
    flows: null,
    actions: null,
    devices: null,
    lastFetched: {
      flows: null,
      actions: null,
      devices: null,
    }
  });

  // Function to update cache with new data
  const updateCache = (dataType, data) => {
    setCachedData(prevState => ({
      ...prevState,
      [dataType]: data,
      lastFetched: {
        ...prevState.lastFetched,
        [dataType]: new Date().getTime()
      }
    }));
  };

  // Function to get data from cache if fresh, or null if stale
  const getCache = (dataType, maxAgeMs = 30000) => { // Default 30 seconds cache
    const lastFetched = cachedData.lastFetched[dataType];
    const data = cachedData[dataType];
    
    if (!lastFetched || !data) return null;
    
    const now = new Date().getTime();
    const age = now - lastFetched;
    
    return age < maxAgeMs ? data : null;
  };

  // Function to clear cache for a specific data type or all
  const clearCache = (dataType = null) => {
    if (dataType) {
      setCachedData(prevState => ({
        ...prevState,
        [dataType]: null,
        lastFetched: {
          ...prevState.lastFetched,
          [dataType]: null
        }
      }));
    } else {
      setCachedData({
        flows: null,
        actions: null,
        devices: null,
        lastFetched: {
          flows: null,
          actions: null,
          devices: null
        }
      });
    }
  };

  // Value object to be provided by the context
  useEffect(() => {
    const reset = () => clearCache('devices');
    window.addEventListener('sb-algo-access-reset', reset);
    return () => window.removeEventListener('sb-algo-access-reset', reset);
  }, []);

  const value = {
    getCache,
    updateCache,
    clearCache
  };

  return (
    <EventSystemCacheContext.Provider value={value}>
      {children}
    </EventSystemCacheContext.Provider>
  );
};

export default EventSystemCacheProvider;
